'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import { Button } from '@/components/ui';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import checkImgSrc from '@/public/assets/icons/check.svg';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations';
import { InventoryStatusModal } from '../InventoryStatus';
import useInventoryStatus from '@/hooks/useInventoryStatus';
import classNames from 'classnames';
import { InventoryStatusCode, ShopifyProduct } from '@/lib/shopify';

interface SiblingsAddToCartCtaProps {
  isProductAddedToCart?: boolean;
  isLoading: boolean;
  handleRemoveProductFromCart: () => void;
  handleAddProductToCart: () => void;
  translations: TypeSubscriberShopPageTranslations;
  inventoryStatus: InventoryStatusCode | null;
  shopifyProduct: ShopifyProduct;
}

const SiblingsAddToCartCta = ({
  isProductAddedToCart,
  isLoading,
  handleAddProductToCart,
  handleRemoveProductFromCart,
  translations,
  inventoryStatus,
  shopifyProduct,
}: SiblingsAddToCartCtaProps) => {
  const {
    addToCartCta,
    addedCta,
    removeCta,
    addingCta,
    removingCta,
    keepShoppingCta,
  } = translations;
  const [isInventoryStatusModalOpen, setIsInventoryStatusModalOpen] =
    useState(false);
  const [ctaText, ctaClasses, isCtaDisabled, modalTitle, modalDescription] =
    useInventoryStatus(shopifyProduct, false);
  const isProductOutOfStock =
    inventoryStatus === InventoryStatusCode.OUT_OF_STOCK;

  return (
    <div>
      {isProductAddedToCart ? (
        <div className="flex w-full items-center justify-between">
          <div className="flex">
            <Image
              src={checkImgSrc}
              alt="Check icon"
              height={24}
              width={24}
              className="h-[24px] w-[24px]"
            />
            <p className="ml-3 text-indigo">{addedCta}</p>
          </div>
          <Button
            ctaText={isLoading ? removingCta : removeCta}
            onClick={handleRemoveProductFromCart}
            size={BUTTON_SIZES.md}
            style={BUTTON_STYLES.link}
            classes="text-lg"
            disabled={isCtaDisabled || isLoading}
          />
        </div>
      ) : (
        <Button
          ctaText={isLoading ? addingCta : ctaText || addToCartCta}
          onClick={() => {
            isProductOutOfStock
              ? setIsInventoryStatusModalOpen(true)
              : handleAddProductToCart();
          }}
          size={BUTTON_SIZES.md}
          style={BUTTON_STYLES.secondary}
          classes={classNames('w-full', ctaClasses)}
          disabled={isCtaDisabled || isLoading}
        />
      )}

      {isProductOutOfStock && (
        <InventoryStatusModal
          isOpen={isInventoryStatusModalOpen}
          handleClose={() => setIsInventoryStatusModalOpen(false)}
          ctaText={keepShoppingCta}
          title={modalTitle}
          description={modalDescription}
        />
      )}
    </div>
  );
};

export default SiblingsAddToCartCta;
